import { useLocale } from '../i18n/LocaleContext';
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import CTA from '../components/CTA';
import SEO from '../components/SEO';

// Import images
import step1Image from '../assets/how_it_works_1.png';
import step2Image from '../assets/how_it_works_2.png';
import step3Image from '../assets/how_it_works_3.png';
import step4Image from '../assets/how_it_works_4.png';
import step5Image from '../assets/how_it_works_5.png';
import step6Image from '../assets/how_it_works_6.png';
import step7Image from '../assets/how_it_works_7.png';
import step8Image from '../assets/how_it_works_8.png';
import step9Image from '../assets/how_it_works_9.png';


const HowItWorksPage = () => {
  const { t, path } = useLocale();
  const [activeTab, setActiveTab] = useState('standard');

  return (
    <Container>
      <SEO
        title={t("nav.how")}
        description={t("seo.guide")}
        canonicalPath="/how-to-use"
      />
      <Header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1>{t("nav.how")}</h1>
        <p>{t('guide.description')}</p>
        <WatchGuideLink
          href={`${path("/")}#walkthrough`}
        >
          {t('guide.watch')}
        </WatchGuideLink>
      </Header>

      <TabContainer>
        <Tab
          isActive={activeTab === 'standard'}
          onClick={() => setActiveTab('standard')}
        >
          {t('guide.pc')}
        </Tab>
        <Tab
          isActive={activeTab === 'nomicrophone'}
          onClick={() => setActiveTab('nomicrophone')}
        >
          {t('guide.phone')}
        </Tab>
      </TabContainer>

      <p style={{ textAlign: "center" }}>{t("guide.screenshots")}</p>
      <TabContent>
        {activeTab === 'standard' ? (
          <StandardWorkflowContent />
        ) : (
          <NoMicrophoneContent />
        )}
      </TabContent>

      <CTAWrapper>
        <CTA />
      </CTAWrapper>
    </Container>
  );
};

const StandardWorkflowContent = () => {
  const { t } = useLocale();
  const steps = [
    {
      id: 1,
      title: t("guide.micTitle"),
      description: t("guide.micBody"),
      imagePosition: "left",
      image: step1Image
    },
    {
      id: 2,
      title: t("guide.startTitle"),
      description: t("guide.startBody"),
      imagePosition: "right",
      image: step2Image
    },
    {
      id: 3,
      title: t("guide.speakTitle"),
      description: t("guide.speakBody"),
      imagePosition: "left",
      image: step3Image
    },
    {
      id: 4,
      title: t("guide.stopTitle"),
      description: t("guide.stopBody"),
      imagePosition: "right",
      image: step4Image
    },
    {
      id: 5,
      title: t("guide.copyTitle"),
      description: t("guide.copyBody"),
      imagePosition: "left",
      image: step5Image
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <StepsContainer>
        {steps.map((step) => (
          <StepSection
            key={step.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            reverse={step.imagePosition === "right"}
          >
            <ImageContainer>
              <StepImage src={step.image} alt={step.title} />
            </ImageContainer>
            <ContentContainer>
              <StepNumber>{step.id}</StepNumber>
              <h2>{step.title}</h2>
              <p>{step.description}</p>
            </ContentContainer>
          </StepSection>
        ))}
      </StepsContainer>
    </motion.div>
  );
};

const NoMicrophoneContent = () => {
  const { t } = useLocale();
  const steps = [
    {
      id: 1,
      title: t("guide.mobileLoginTitle"),
      description: t("guide.mobileLoginBody"),
      imagePosition: "left",
      image: step6Image  // Placeholder image
    },
    {
      id: 2,
      title: t("guide.mobileNotesTitle"),
      description: t("guide.mobileNotesBody"),
      imagePosition: "right",
      image: step7Image  // Placeholder image
    },
    {
      id: 3,
      title: t("guide.pcLoginTitle"),
      description: t("guide.pcLoginBody"),
      imagePosition: "left",
      image: step9Image  // Placeholder image
    },
    {
      id: 4,
      title: t("guide.refreshTitle"),
      description: t("guide.refreshBody"),
      imagePosition: "right",
      image: step8Image  // Placeholder image
    },
    {
      id: 5,
      title: t("guide.copyTitle2"),
      description: t("guide.copyBody"),
      imagePosition: "left",
      image: step5Image  // Placeholder image
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <StepsContainer>
        {steps.map((step) => (
          <StepSection
            key={step.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            reverse={step.imagePosition === "right"}
          >
            <ImageContainer>
              <StepImage src={step.image} alt={step.title} />
            </ImageContainer>
            <ContentContainer>
              <StepNumber>{step.id}</StepNumber>
              <h2>{step.title}</h2>
              <p>{step.description}</p>
            </ContentContainer>
          </StepSection>
        ))}
      </StepsContainer>
    </motion.div>
  );
};

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;

  h1 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: #2a2a2a;
  }

  p {
    font-size: 1.2rem;
    color: #666;
    max-width: 600px;
    margin: 0 auto;
  }
`;

const WatchGuideLink = styled.a`
  display: inline-block;
  margin-top: 1.25rem;
  color: var(--primary);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: var(--primary-dark);
  }
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4rem;
  margin-bottom: 4rem;
`;

const StepSection = styled(motion.div)`
  display: flex;
  flex-direction: ${props => props.reverse ? 'row-reverse' : 'row'};
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ImageContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const StepImage = styled.img`
  width: 300px;
  height: 300px;
  object-fit: cover;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: scale(1.03);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 300px;
    height: auto;
    aspect-ratio: 1/1;
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  position: relative;
  padding-inline-start: 20px;

  h2 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
    color: #2a2a2a;
    margin-inline-start: 10px;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.6;
    color: #666;
  }
`;

const StepNumber = styled.div`
  position: absolute;
  top: -15px;
  inset-inline-start: -30px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #4a6cf7;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: bold;
`;

const CTAWrapper = styled.div`
  margin-top: 2rem;
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
  border-bottom: 1px solid #eaeaea;
`;

const Tab = styled.button`
  padding: 15px 30px;
  font-size: 1.1rem;
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.isActive ? '#4a6cf7' : '#666'};
  border-bottom: 3px solid ${props => props.isActive ? '#4a6cf7' : 'transparent'};
  transition: all 0.3s ease;

  &:hover {
    color: #4a6cf7;
  }

  @media (max-width: 576px) {
    padding: 10px 15px;
    font-size: 1rem;
  }
`;

const TabContent = styled.div`
  min-height: 400px;
`;

export default HowItWorksPage;
